import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Carrello from '../../interface/Carrello';
import {
  Caparra,
  PagamentoAccontoInterface,
  PagamentoAssegnoInterface,
  PagamentoBancheInterface,
  PagamentoBuonoInterface,
  PagamentoCodiceSTSInterface,
  PagamentoDialogProssimaFornituraInterface,
  PagamentoFinanziamentoInterface,
  PagamentoFondoInterface,
  PagamentoInputBoxInterface,
  PagamentoLottoInterface,
  PagamentoSelezioneDocumentoInterface,
  PagamentoSelezioneNomeDocumentoInterface,
  PagamentoStampaScontrinoInterface,
  RichiestaDatiDocumentoResoInterface,
} from '../../interface/PagamentoInterfaces';
import CarrelloPagamentoData from '../../interface/pagamento_utils/CarrelloPagamentoDataInterface';
import { pagamentoDefaultJson } from '../../interface/pagamento_utils/pagamentoDefaultJson';
import { Iva_table_interface } from '@bludata/common-interfaces';

export interface CarrelloSliceState {
  carrello: Carrello[];
  pagamento: CarrelloPagamentoData;
  pagamentoCassaNonCollegata: boolean;
  eseguiConfermaCodiceFiscale: boolean;
  eseguiPagamentoAutomatico: boolean;
  firstShow: boolean;
  newFocus: string;
  returnButton: string;
  acconto: PagamentoAccontoInterface;
  stampaScontrino: PagamentoStampaScontrinoInterface;
  caparra: Caparra;
  banca: PagamentoBancheInterface;
  assegno: PagamentoAssegnoInterface;
  buono: PagamentoBuonoInterface;
  finanziamento: PagamentoFinanziamentoInterface;
  fondo: PagamentoFondoInterface;
  codiceSTS: PagamentoCodiceSTSInterface;
  inputBox: PagamentoInputBoxInterface;
  lotto: PagamentoLottoInterface;
  mostraGestioneReso: boolean;
  totalecarrello: number;
  importopagato: number;
  importototale: number;
  infoReso?: RichiestaDatiDocumentoResoInterface;
  selezioneDocumento?: PagamentoSelezioneDocumentoInterface;
  prossimaFornitura?: PagamentoDialogProssimaFornituraInterface;
  selezioneNomeDocumento?: PagamentoSelezioneNomeDocumentoInterface;
}

const initialState: CarrelloSliceState = {
  carrello: [],
  pagamento: pagamentoDefaultJson,
  pagamentoCassaNonCollegata: false,
  eseguiConfermaCodiceFiscale: false,
  eseguiPagamentoAutomatico: false,
  firstShow: false,
  newFocus: '',
  returnButton: '',
  mostraGestioneReso: false,
  totalecarrello: 0,
  importopagato: 0,
  importototale: 0,
  acconto: {
    codiceFornitura: '',
    codiceIva: '',
    codiceSts: '',
    descrizione: '',
    importo: 0,
    returnButton: '',
    tipoFornitura: 0,
  },
  stampaScontrino: {
    returnButton: '',
    infoScontrino: {
      numero: '',
      data: '',
      dispositivo: '',
    },
  },
  caparra: {
    descrizione: '',
    importo: 0,
    importoMax: 0,
    returnButton: '',
  },
  banca: {
    tipoPagamento: '',
    totale: 0,
    listaBanche: [],
    indice: 0,
  },
  assegno: {
    bancaEmittente: '',
    numeroAssegno: '',
    returnbutton: '',
  },
  buono: {
    nomeBuono: '',
    numeroBuono: '',
    returnbutton: '',
  },
  finanziamento: {
    returnbutton: '',
    istitutoDiCredito: '',
    codiceIstituto: '',
    numeroContratto: '',
  },
  fondo: {
    returnbutton: '',
    codiceFondo: '',
    bancaRif: '',
  },
  codiceSTS: {
    returnbutton: '',
    descrizioneCarrello: '',
    codiceSTS: '',
    tuttiND: false,
  },
  inputBox: {
    returnbutton: '',
    input: '',
    caption: '',
    text: '',
    defaultInput: '',
    maxInputLength: 0,
    minInputLength: 0,
    numberOnly: false,
    allowCancel: false,
  },
  lotto: {
    lotti: [],
    descrizioneArticolo: '',
    quantita: 1,
    consentiModificaQuantita: true,
    quantitaMassima: 1,
  },
};

export const getDescrizioneIva = (iva: Iva_table_interface | undefined) => {
  if (!iva) return '';
  return `${iva.codiceFiliale} - ${iva.descrizioneBreve}`;
};

const CarrelloSlice = createSlice({
  name: 'carrello',
  initialState,
  reducers: {
    setCarrello(state: CarrelloSliceState, action: PayloadAction<Carrello[]>) {
      state.carrello = action.payload;
    },
    setCarrelloProduct(
      state: CarrelloSliceState,
      action: PayloadAction<Carrello>,
    ) {
      const removeItem = state.carrello.filter(
        (x) => x.id !== action.payload.id,
      );
      state.carrello = [...removeItem, action.payload];
    },
    setCarrelloPagamentoData(
      state: CarrelloSliceState,
      action: PayloadAction<CarrelloPagamentoData>,
    ) {
      state.pagamento = action.payload;
    },
    setPagamentoCassaNonCollegata(
      state: CarrelloSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.pagamentoCassaNonCollegata = action.payload;
    },
    setStampaScontrino(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoStampaScontrinoInterface>,
    ) {
      state.stampaScontrino = action.payload;
    },
    setAcconto(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoAccontoInterface>,
    ) {
      state.acconto = action.payload;
    },
    setCaparra(state: CarrelloSliceState, action: PayloadAction<Caparra>) {
      state.caparra = action.payload;
    },
    setBanca(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoBancheInterface>,
    ) {
      state.banca = action.payload;
    },
    setAssegno(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoAssegnoInterface>,
    ) {
      state.assegno = action.payload;
    },
    setBuono(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoBuonoInterface>,
    ) {
      state.buono = action.payload;
    },
    setFinanziamento(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoFinanziamentoInterface>,
    ) {
      state.finanziamento = action.payload;
    },
    setFondo(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoFondoInterface>,
    ) {
      state.fondo = action.payload;
    },
    setCodiceSTS(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoCodiceSTSInterface>,
    ) {
      state.codiceSTS = action.payload;
    },
    setLotto(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoLottoInterface>,
    ) {
      state.lotto = action.payload;
    },
    setInputBox(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoInputBoxInterface>,
    ) {
      state.inputBox = action.payload;
    },
    setSelezioneNomeDocumentoPagamento(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoSelezioneNomeDocumentoInterface>,
    ) {
      state.selezioneNomeDocumento = action.payload;
    },
    setMostraGestioneReso(
      state: CarrelloSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.mostraGestioneReso = action.payload;
    },
    setTotaleCarrello(
      state: CarrelloSliceState,
      action: PayloadAction<number>,
    ) {
      state.totalecarrello = action.payload;
    },
    setImportoPagato(state: CarrelloSliceState, action: PayloadAction<number>) {
      state.importopagato = action.payload;
    },
    setImportoTotale(state: CarrelloSliceState, action: PayloadAction<number>) {
      state.importototale = action.payload;
    },
    setInfoReso(
      state: CarrelloSliceState,
      action: PayloadAction<RichiestaDatiDocumentoResoInterface>,
    ) {
      state.infoReso = action.payload;
    },
    setSelezioneDocumento(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoSelezioneDocumentoInterface>,
    ) {
      state.selezioneDocumento = action.payload;
    },
    setProssimaFornitura(
      state: CarrelloSliceState,
      action: PayloadAction<PagamentoDialogProssimaFornituraInterface>,
    ) {
      state.prossimaFornitura = action.payload;
    },
    setPagamentoDataObject(
      state: CarrelloSliceState,
      action: PayloadAction<{
        pagamentoParams: CarrelloPagamentoData;
        eseguiConfermaCodiceFiscale: false;
        eseguiPagamentoAutomatico: false;
        firstShow: false;
        newFocus: '';
        returnButton: '';
      }>,
    ) {
      state.pagamento = action.payload.pagamentoParams;
      state.eseguiConfermaCodiceFiscale =
        action.payload.eseguiConfermaCodiceFiscale;
      state.eseguiPagamentoAutomatico =
        action.payload.eseguiPagamentoAutomatico;
      state.firstShow = action.payload.firstShow;
      state.newFocus = action.payload.newFocus;
      state.returnButton = action.payload.returnButton;
    },
  },
});

export const {
  setCarrelloPagamentoData,
  setCarrello,
  setInputBox,
  setImportoPagato,
  setImportoTotale,
  setTotaleCarrello,
  setAcconto,
  setPagamentoDataObject,
  setPagamentoCassaNonCollegata,
  setSelezioneNomeDocumentoPagamento,
  setSelezioneDocumento,
  setStampaScontrino,
  setCarrelloProduct,
  setCaparra,
  setBuono,
  setBanca,
  setMostraGestioneReso,
  setAssegno,
  setLotto,
  setFinanziamento,
  setFondo,
  setInfoReso,
  setProssimaFornitura,
  setCodiceSTS,
} = CarrelloSlice.actions;

export default CarrelloSlice.reducer;
